/**
 * Email Verification Page
 * 
 * Shown when authenticated user has not verified their email.
 * Per Δ-B2: Block /app/* access until email verified.
 */

import { useState } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { useAuthState } from '../../config/auth';
import { Link } from 'react-router-dom';
import { ROUTES } from '@threadlock/routing';

export default function VerifyEmailPage() {
  const { user } = useAuthState();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    if (!user) return;
    
    setSending(true);
    setError('');
    
    try {
      await sendEmailVerification(user);
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-neutral-900">Verify your email</h2>
          <p className="mt-4 text-sm text-neutral-600">
            We've sent a verification link to <strong>{user?.email}</strong>.
            Please check your email and click the link to verify your account.
          </p>
          <p className="mt-4 text-sm text-neutral-600">
            You must verify your email before accessing the application.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {sent && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-sm">
            Verification email sent! Please check your inbox.
          </div>
        )}

        <div className="space-y-4">
          <button
            onClick={handleResend}
            disabled={sending || sent}
            className="w-full flex justify-center py-2 px-4 border border-neutral-300 rounded-md shadow-sm text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending...' : sent ? 'Email sent!' : 'Resend verification email'}
          </button>

          <div className="text-center text-sm">
            <Link to={ROUTES.LOGIN} className="text-neutral-600 hover:text-neutral-900">
              Back to login
            </Link>
          </div>
        </div>

        <div className="mt-6 p-4 bg-neutral-100 rounded-md">
          <p className="text-xs text-neutral-600">
            <strong>Note:</strong> After clicking the verification link in your email, 
            please return here and refresh the page or log in again.
          </p>
        </div>
      </div>
    </div>
  );
}
